import { auth } from 'firebase';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  signInAnonymously,
  linkWithCredential,
  EmailAuthProvider,
  getAuth,
  getIdToken,
  isSignInWithEmailLink,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  updatePassword,
  verifyPasswordResetCode,
} from 'firebase/auth';

export type NewUser = {
  name: string;
  email: string | null;
  phoneNumber: string;
  id: string;
};

import axios from 'axios';
import type { User } from 'firebase/auth';
import { updateProfile } from 'firebase/auth';
import { doc, getFirestore, setDoc } from 'firebase/firestore';

export interface IAuthService {
  [key: string]: any; //TODO: define interface
}

export enum ROLES {
  USER = 'user',
  MERCHANT = 'merchant',
  ADMIN = 'admin',
}

export enum ROLE_TYPES {
  USER = 'User',
  SUPER_ADMIN = 'SuperAdmin',
  MERCHANT_ADMIN = 'MerchantAdmin',
  RESTAURANT_MANAGER = 'RestaurantManager',
  ORDER_ADMIN = 'OrderAdmin',
  ANONYMOUS = 'Anonymous',
}

export const ensureAnonymousUser = async () => {
  const authInstance = getAuth();

  // Wait for Firebase Auth to initialize and restore any existing user
  return new Promise((resolve) => {
    const unsubscribe = authInstance.onAuthStateChanged(async (user) => {
      unsubscribe(); // Clean up the listener

      if (!user) {
        // Only create anonymous user if there's genuinely no user
        const db = getFirestore();
        const { user: anonUser } = await signInAnonymously(authInstance);
        await setDoc(
          doc(db, 'users', anonUser.uid),
          {
            uid: anonUser.uid,
            role: ROLES.USER,
            roleType: ROLE_TYPES.USER,
            createdAt: new Date(),
          },
          { merge: true }
        );
        resolve(anonUser);
      } else {
        // User already exists (either authenticated or anonymous)
        resolve(user);
      }
    });
  });
};

export const login = async (
  email: string,
  password: string,
  rememberMe: boolean
) => {
  setPersistence(
    auth,
    rememberMe ? browserLocalPersistence : browserSessionPersistence
  );

  return signInWithEmailAndPassword(auth, email, password);
};

export const signup = async (
  email: string,
  password: string,
  name: string,
  phoneNumber: string,
  rememberMe?: boolean
) => {
  const auth = getAuth();
  const db = getFirestore();

  try {
    await setPersistence(
      auth,
      rememberMe ? browserLocalPersistence : browserSessionPersistence
    );

    let userCredential;
    if (auth.currentUser && auth.currentUser.isAnonymous) {
      const credential = EmailAuthProvider.credential(email, password);
      userCredential = await linkWithCredential(auth.currentUser, credential);
    } else {
      userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
    }
    const user = userCredential.user;

    await updateProfile(user, {
      displayName: name,
    });

    await setDoc(
      doc(db, 'users', user.uid),
      {
        uid: user.uid,
        email: user.email,
        displayName: name,
        phoneNumber: phoneNumber,
        role: ROLES.USER,
        roleType: ROLE_TYPES.USER,
        createdAt: new Date(),
      },
      { merge: true }
    );
    return user;
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export const logout = () => {
  signOut(auth);
  window.location.assign('/');
};

export const logoutWithoutRedirect = (url: string) => {
  signOut(auth);
  window.location.assign(url);
};

export const sendResetPasswordEmail = async (email: string) => {
  try {
    sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error(error);
  }
};

export const verifyResetPasswordCode = async (code?: string) => {
  if (!code) return false;
  try {
    await verifyPasswordResetCode(auth, code);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const resetPassword = async (code: string, newPassword: string) => {
  try {
    await confirmPasswordReset(auth, code, newPassword);
  } catch (error) {
    console.error(error);
  }
};

// TODO: this doesn't need to be async
export const getCurrentUser = (): User | null => {
  return getAuth().currentUser;
};

// TODO: refactor this into a provider
export const getCurrentUserRole = async (): Promise<any> => {
  // Wait for Firebase Auth to initialize
  console.log('👤 [DEBUG] getCurrentUserRole called');
  return new Promise((resolve, reject) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe(); // Clean up the listener
      console.log('👤 [DEBUG] Auth state changed in getCurrentUserRole:', {
        hasUser: !!user,
        isAnonymous: user?.isAnonymous,
        uid: user?.uid
      });

      if (user) {
        console.log('👤 [DEBUG] Getting user role from token claims');
        user
          .getIdTokenResult(true) // Force refresh here too
          .then(({ claims }) => {
            const { role, roleType } = claims;
            console.log('👤 [DEBUG] User role retrieved:', { role, roleType });
            resolve({
              role,
              roleType,
            });
          })
          .catch((error) => {
            console.error('👤 [DEBUG] Error getting user role:', error);
            // Only logout on specific auth errors, not network issues
            if (
              error.code === 'auth/invalid-user-token' ||
              error.code === 'auth/user-token-expired'
            ) {
              console.log('👤 [DEBUG] Role token expired, logging out');
              AuthService.logout();
            }
            reject(error);
          });
      } else {
        // User is genuinely not authenticated, return null instead of logging out
        console.log('👤 [DEBUG] No user found for role check, returning null');
        resolve(null);
      }
    });
  });
};

export const getUser = async () => {
  // /users/user/{consumerId}
  const token = await getToken();
  //const consumerId = FirebaseManager.auth.currentUserID();
  const response = await axios.get(`/users/user`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const updateUser = async (user: NewUser) => {
  const token = await getToken();
  const userPayload = {
    consumerId: user.id,
    name: user.name,
    email: user.email,
    phoneNumber: user.phoneNumber,
    isOnboarded: true,
  };
  console.log('userPayload', userPayload);
  console.log('token', token);
  const response = await axios.post('/users/updateUser', userPayload, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  console.log('userPaylod response', response.data);

  return response.data;
};

export const getToken = async (): Promise<string | undefined> => {
  // Wait for Firebase Auth to initialize
  console.log('🔑 [DEBUG] getToken called');
  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe(); // Clean up the listener
      console.log('🔑 [DEBUG] Auth state changed in getToken:', {
        hasUser: !!user,
        isAnonymous: user?.isAnonymous,
        uid: user?.uid
      });

      if (user) {
        console.log('🔑 [DEBUG] Attempting to get token with forceRefresh=true');
        user
          .getIdToken(true) // Force refresh to get a fresh token
          .then((token) => {
            console.log('🔑 [DEBUG] Token retrieved successfully:', token ? 'YES' : 'NO');
            resolve(token);
          })
          .catch((error) => {
            console.error('🔑 [DEBUG] Error getting token:', error);
            // Only logout on specific auth errors, not network issues
            if (
              error.code === 'auth/invalid-user-token' ||
              error.code === 'auth/user-token-expired'
            ) {
              console.log('🔑 [DEBUG] Token expired, logging out');
              AuthService.logout();
            }
            resolve(undefined);
          });
      } else {
        // User is genuinely not authenticated
        console.log('🔑 [DEBUG] No user found, returning undefined');
        resolve(undefined);
      }
    });
  });
};

export const getInvites = async () => {
  const currentUser = getCurrentUser();
  if (currentUser) {
    try {
      const token = await currentUser.getIdToken();
      const response = await axios.get(`/invites`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (err) {
      console.log(err);
    }
  } else {
    console.log('No user found');
  }
};

export const isAdmin = async (): Promise<boolean> => {
  const { role } = await getCurrentUserRole();
  return role === ROLES.ADMIN;
};

export const signInWithLink = async (link: string) => {
  const encodedEmail = new URL(link).searchParams.get('email');
  const decodedEmail = atob(encodedEmail || '');
  return signInWithEmailLink(auth, decodedEmail, link);
};

export const isValidSignInLink = (link: string) => {
  return isSignInWithEmailLink(auth, link);
};

export const changePassword = async (password: string) => {
  const currentUser = getCurrentUser();
  if (currentUser) {
    await updatePassword(currentUser, password);
  }
};

const AuthService = {
  login,
  logout,
  logoutWithoutRedirect,
  sendResetPasswordEmail,
  verifyResetPasswordCode,
  resetPassword,
  getCurrentUser,
  getToken,
  getCurrentUserRole,
  isAdmin,
  signInWithLink,
  isValidSignInLink,
  changePassword,
  ensureAnonymousUser,
  signup,
  getInvites,
  getUser,
  updateUser,
};

export default AuthService;
